import React from 'react';
import { Box, Grid, Typography, TextField, TableHead, TableCell, TableRow } from '@mui/material';
import {
  HeaderBoxLeft,
  HeaderBoxRight,
  HeaderTypography,
  IbrsLabelBox,
  BulkyIbrsLabelBox,
  SubLabelBox,
  InputContainerBox,
  LastInputContainerBox,
  IbrsTextField,
  LabelTypography,
} from './IbrsRateTable.styled';

interface IbrsRateTableProps {
  title: string;
  normalIbrsWeight: string;
  normalIbrsItems: string;
  bulkyIbrsWeight: string;
  bulkyIbrsItems: string;
  onNormalIbrsWeightChange: (value: string) => void;
  onNormalIbrsItemsChange: (value: string) => void;
  onBulkyIbrsWeightChange: (value: string) => void;
  onBulkyIbrsItemsChange: (value: string) => void;
  normalIbrsWeightError?: boolean;
  normalIbrsItemsError?: boolean;
  bulkyIbrsWeightError?: boolean;
  bulkyIbrsItemsError?: boolean;
  type?: 'inbound' | 'outbound';
}

function IbrsRateTable({
  title,
  normalIbrsWeight,
  normalIbrsItems,
  bulkyIbrsWeight,
  bulkyIbrsItems,
  onNormalIbrsWeightChange,
  onNormalIbrsItemsChange,
  onBulkyIbrsWeightChange,
  onBulkyIbrsItemsChange,
  normalIbrsWeightError = false,
  normalIbrsItemsError = false,
  bulkyIbrsWeightError = false,
  bulkyIbrsItemsError = false,
  type,
  }: IbrsRateTableProps) {
    const backgroundColor = type === 'inbound' ? 'rgb(230, 245, 230)' : type === 'outbound' ? 'rgb(255, 240, 240)' : 'transparent';
    
    return (
      <Box 
        data-testid="ibrs-rate-table-container"
        sx={{ backgroundColor }}
      >
      {/* Table Header */}
      <Grid container sx={{ width: '100%' }} data-testid="ibrs-rate-table-header">
        <Grid item xs={6} sx={{ width: '50%' }} data-testid="ibrs-rate-table-title-column">
          <HeaderBoxLeft data-testid="ibrs-rate-table-title-box">
            <HeaderTypography data-testid="ibrs-rate-table-title-text">
              {title}
            </HeaderTypography>
          </HeaderBoxLeft>
        </Grid>
        <Grid item xs={6} sx={{ width: '50%' }} data-testid="ibrs-rate-table-rate-column">
          <HeaderBoxRight data-testid="ibrs-rate-table-rate-box">
            <HeaderTypography data-testid="ibrs-rate-table-rate-text">
              IBRS Rate
            </HeaderTypography>
          </HeaderBoxRight>
        </Grid>
      </Grid>

      {/* Normal IBRS Section */}
      <Grid container data-testid="ibrs-rate-table-normal-section">
        {/* Normal IBRS Label - spans multiple rows */}
        <Grid item xs={3} data-testid="ibrs-rate-normal-label-column">
          <IbrsLabelBox data-testid="ibrs-rate-normal-label-box">
            <LabelTypography variant="body2" data-testid="ibrs-rate-normal-label-text">
              Normal IBRS
            </LabelTypography>
          </IbrsLabelBox>
        </Grid>

        {/* Normal IBRS sub-columns */}
        <Grid item xs={3} data-testid="ibrs-rate-normal-sub-labels">
          {/* Weight Row */}
          <SubLabelBox data-testid="ibrs-rate-normal-weight-label">
            <Typography variant="body2">
              weight
            </Typography>
          </SubLabelBox>
          {/* Items Row */}
          <SubLabelBox data-testid="ibrs-rate-normal-items-label">
            <Typography variant="body2">
              items
            </Typography>
          </SubLabelBox>
        </Grid>

        {/* IBRS Rate inputs for Normal IBRS */}
        <Grid item xs={6} data-testid="ibrs-rate-normal-inputs">
          {/* Weight Input */}
          <InputContainerBox data-testid="ibrs-rate-normal-weight-input-container">
            <IbrsTextField
              data-testid="ibrs-rate-normal-weight-input"
              size="small"
              fullWidth
              type="text"
              required
              error={normalIbrsWeightError}
              value={normalIbrsWeight}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*(\.\d{0,3})?$/.test(val)) {
                  onNormalIbrsWeightChange(val);
                }
              }}
              onBlur={(e) => {
                const sanitized = e.target.value.trim();
                if (/^\d*(\.\d{0,3})?$/.test(sanitized) && sanitized !== '') {
                  const num = parseFloat(sanitized);
                  if (!isNaN(num)) onNormalIbrsWeightChange(num.toFixed(3));
                }
              }}
              inputProps={{
                placeholder: "0.000",
                inputMode: "decimal",
                pattern: '^\\d*(\\.\\d{0,3})?$',
              }}
            />
          </InputContainerBox>
          {/* Items Input */}
          <InputContainerBox data-testid="ibrs-rate-normal-items-input-container">
            <IbrsTextField
              data-testid="ibrs-rate-normal-items-input"
              size="small"
              fullWidth
              type="text"
              required
              error={normalIbrsItemsError}
              value={normalIbrsItems}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*(\.\d{0,3})?$/.test(val)) {
                  onNormalIbrsItemsChange(val);
                }
              }}
              onBlur={(e) => {
                const sanitized = e.target.value.trim();
                if (/^\d*(\.\d{0,3})?$/.test(sanitized) && sanitized !== '') {
                  const num = parseFloat(sanitized);
                  if (!isNaN(num)) onNormalIbrsItemsChange(num.toFixed(3));
                }
              }}
              inputProps={{
                placeholder: "0.000",
                inputMode: "decimal",
                pattern: '^\\d*(\\.\\d{0,3})?$',
              }}
            />
          </InputContainerBox>
        </Grid>
      </Grid>

      {/* Bulky IBRS Section */}
      <Grid container data-testid="ibrs-rate-table-bulky-section">
        {/* Bulky IBRS Label - spans multiple rows */}
        <Grid item xs={3} data-testid="ibrs-rate-bulky-label-column">
          <BulkyIbrsLabelBox data-testid="ibrs-rate-bulky-label-box">
            <LabelTypography variant="body2" data-testid="ibrs-rate-bulky-label-text">
              Bulky IBRS
            </LabelTypography>
          </BulkyIbrsLabelBox>
        </Grid>

        {/* Bulky IBRS sub-columns */}
        <Grid item xs={3} data-testid="ibrs-rate-bulky-sub-labels">
          {/* Weight Row */}
          <SubLabelBox data-testid="ibrs-rate-bulky-weight-label">
            <Typography variant="body2">
              weight
            </Typography>
          </SubLabelBox>
          {/* Items Row */}
          <SubLabelBox data-testid="ibrs-rate-bulky-items-label">
            <Typography variant="body2">
              items
            </Typography>
          </SubLabelBox>
        </Grid>

        {/* IBRS Rate inputs for Bulky IBRS */}
        <Grid item xs={6} data-testid="ibrs-rate-bulky-inputs">
          {/* Weight Input */}
          <InputContainerBox data-testid="ibrs-rate-bulky-weight-input-container">
            <IbrsTextField
              data-testid="ibrs-rate-bulky-weight-input"
              size="small"
              fullWidth
              type="text"
              required
              error={bulkyIbrsWeightError}
              value={bulkyIbrsWeight}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*(\.\d{0,3})?$/.test(val)) {
                  onBulkyIbrsWeightChange(val);
                }
              }}
              onBlur={(e) => {
                const sanitized = e.target.value.trim();
                if (/^\d*(\.\d{0,3})?$/.test(sanitized) && sanitized !== '') {
                  const num = parseFloat(sanitized);
                  if (!isNaN(num)) onBulkyIbrsWeightChange(num.toFixed(3));
                }
              }}
              inputProps={{
                placeholder: "0.000",
                inputMode: "decimal",
                pattern: '^\\d*(\\.\\d{0,3})?$',
              }}
            />
          </InputContainerBox>
          {/* Items Input */}
          <LastInputContainerBox data-testid="ibrs-rate-bulky-items-input-container">
            <IbrsTextField
              data-testid="ibrs-rate-bulky-items-input"
              size="small"
              fullWidth
              type="text"
              required
              error={bulkyIbrsItemsError}
              value={bulkyIbrsItems}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*(\.\d{0,3})?$/.test(val)) {
                  onBulkyIbrsItemsChange(val);
                }
              }}
              onBlur={(e) => {
                const sanitized = e.target.value.trim();
                if (/^\d*(\.\d{0,3})?$/.test(sanitized) && sanitized !== '') {
                  const num = parseFloat(sanitized);
                  if (!isNaN(num)) onBulkyIbrsItemsChange(num.toFixed(3));
                }
              }}
              inputProps={{
                placeholder: "0.000",
                inputMode: "decimal",
                pattern: '^\\d*(\\.\\d{0,3})?$',
              }}
            />
          </LastInputContainerBox>
        </Grid>
      </Grid>
    </Box>
  );
}

export default IbrsRateTable; 